import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { Form, Input, Select, Button, Spin, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { IoMdInformationCircle } from "react-icons/io";

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/SideNav.css'; 
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav'; 

const { Option } = Select;

const FacultyProfileForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const { id: profileId } = useParams();

    const [initialProfileData, setInitialProfileData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);

    const isViewMode = location.pathname.includes('/view/');
    const isEditMode = !!profileId && !isViewMode;
    const isCreateMode = !profileId;
    const isArchivedView = isViewMode && initialProfileData?.isArchived;

    const [allFacultyAccounts, setAllFacultyAccounts] = useState([]);
    const [availableFacultyAccounts, setAvailableFacultyAccounts] = useState([]);
    const [loadingFaculty, setLoadingFaculty] = useState(false);

    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const toggleSideNav = useCallback(() => {
        setIsSideNavOpen(prev => !prev);
    }, []);

     useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        handleResize(); 
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    const pageTitle = isArchivedView
        ? 'View Archived Faculty Profile'
        : isViewMode
            ? 'View Faculty Profile'
            : isEditMode
                ? 'Edit Faculty Profile'
                : 'Create Faculty Profile';

    const fieldsDisabled = isViewMode || isSubmitting || initialDataLoading;

    const fetchAllFacultyMembers = useCallback(async () => {
        setLoadingFaculty(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/accounts?role=Faculty&status=Active&archived=false`);
            if (!response.ok) throw new Error('Failed to fetch faculty members list');
            const result = await response.json();
            setAllFacultyAccounts(result.data || result || []);
        } catch (error) {
            console.error("Error fetching faculty members:", error);
            if (!isViewMode) toast.error(error.message || 'Could not load faculty members.');
        } finally {
            setLoadingFaculty(false);
        }
    }, [isViewMode, process.env.REACT_APP_API_URL]);

    useEffect(() => {
        const filterAvailableFaculty = async () => {
            if (isCreateMode && allFacultyAccounts.length > 0) {
                setLoadingFaculty(true);
                try {
                    const profilesResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/faculty-profiles`);
                    if (!profilesResponse.ok) throw new Error('Failed to fetch existing profiles for filtering');
                    const existingProfilesResult = await profilesResponse.json();
                    const existingProfiles = existingProfilesResult.data || existingProfilesResult || [];
                    const usedFacultyRefs = existingProfiles.map(p => p.facultyRef?._id || p.facultyRef);
                    const available = allFacultyAccounts.filter(acc => !usedFacultyRefs.includes(acc._id));
                    setAvailableFacultyAccounts(available);
                } catch (error) {
                    console.error("Error filtering available faculty:", error);
                    toast.error("Could not filter faculty list for creation. Displaying all.");
                    setAvailableFacultyAccounts(allFacultyAccounts);
                } finally {
                    setLoadingFaculty(false);
                }
            } else if (isEditMode || isViewMode) {
                setAvailableFacultyAccounts(allFacultyAccounts);
            } else {
                setAvailableFacultyAccounts([]);
            }
        };
        if (allFacultyAccounts.length > 0) {
            filterAvailableFaculty();
        }
    }, [isCreateMode, isEditMode, isViewMode, allFacultyAccounts, process.env.REACT_APP_API_URL]);

    useEffect(() => {
        fetchAllFacultyMembers();
    }, [fetchAllFacultyMembers]);

    useEffect(() => {
        const fetchProfileDetails = async () => {
            setInitialDataLoading(true);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/faculty-profiles/${profileId}`);
                if (!response.ok) {
                    if (response.status === 404) toast.error('Faculty profile not found.');
                    else throw new Error('Failed to fetch faculty profile details');
                    navigate('/user-admin/faculty-profile');
                    return;
                }
                const result = await response.json();
                const profileData = result.data || result;

                if (profileData) {
                    setInitialProfileData(profileData);
                    form.setFieldsValue({
                        facultyRef: profileData.facultyRef?._id || profileData.facultyRef,
                        department: profileData.department,
                        designation: profileData.designation,
                        employmentType: profileData.employmentType,
                        subjectClassificationHandled: profileData.subjectClassificationHandled,
                        gradeLevelHandled: profileData.gradeLevelHandled,
                    });
                } else {
                    toast.error('Faculty profile data not found.');
                    navigate('/user-admin/faculty-profile');
                }
            } catch (error)
             {
                console.error("Error fetching profile details:", error);
                toast.error(error.message || 'Could not load profile details.');
                navigate('/user-admin/faculty-profile');
            } finally {
                setInitialDataLoading(false);
            }
        };
        if (profileId) {
            fetchProfileDetails();
        } else {
            setInitialProfileData(null);
            form.resetFields();
        }
    }, [profileId, form, navigate, process.env.REACT_APP_API_URL, isViewMode]);

    const handleBack = () => navigate('/user-admin/faculty-profile');

    const onFinishFailed = (errorInfo) => {
        console.log('Form Submission Failed:', errorInfo);
        toast.error('Please correct the highlighted errors in the form.');
    };

    const handleSubmit = async (values) => {
        if (isViewMode) return;
        setIsSubmitting(true);
        const payload = {
            facultyRef: values.facultyRef,
            department: values.department,
            designation: values.designation,
            employmentType: values.employmentType,
            subjectClassificationHandled: values.subjectClassificationHandled,
            gradeLevelHandled: values.gradeLevelHandled,
        };
        try {
            const method = isEditMode ? 'PUT' : 'POST';
            const endpoint = isEditMode
                ? `${process.env.REACT_APP_API_URL}/api/admin/faculty-profiles/${profileId}`
                : `${process.env.REACT_APP_API_URL}/api/admin/faculty-profiles`;
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to ${isCreateMode ? 'create' : 'update'} profile.`);
            }
            toast.success(`Faculty profile ${isCreateMode ? 'created' : 'updated'} successfully!`);
            navigate('/user-admin/faculty-profile');
        } catch (error) {
            console.error("Error submitting faculty profile:", error);
            toast.error(error.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state for initial data fetch
    if (initialDataLoading && profileId) {
        return (
            <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
                <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
                <div className="admin-content-wrapper">
                    <div className="admin-sidenav-container">
                        <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                    </div>
                    <main className="admin-main-content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 120px)' }}>
                        <Spin tip="Loading profile data..." size="large" />
                    </main>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    {/* Internal form structure from CreateStrand pattern */}
                    <div className="page-title"> {/* From CreateStrand and Dashboard */}
                        <div className="arrows" onClick={handleBack} role="button" tabIndex={0} onKeyPress={handleBack}> {/* From CreateStrand */}
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">{pageTitle}</p> {/* From CreateStrand and Dashboard */}
                    </div>

                    <Form
                        form={form}
                        labelCol={{ xs: { span: 24 }, sm: { span: 6 } }}
                        wrapperCol={{ xs: { span: 24 }, sm: { span: 14 } }} // Adjusted for content
                        onFinish={handleSubmit}
                        onFinishFailed={onFinishFailed}
                        className="faculty-profile-form-antd" // General class for Antd form styling
                    >
                        <div className="container-columns"> {/* From CreateStrand */}
                            <div className="column"> {/* From CreateStrand */}
                                <div className="group-title"> {/* From CreateStrand */}
                                    <IoMdInformationCircle className="section-icon" />
                                    <p className="section-title">FACULTY PROFILE DETAILS</p>
                                </div>

                                <Form.Item
                                    label="Faculty Member"
                                    name="facultyRef"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select the faculty member!' }]}
                                    tooltip={isCreateMode ? "Only faculty without an existing profile are shown." : "Faculty member cannot be changed after profile creation."}
                                >
                                    <Select
                                        placeholder="Select faculty member"
                                        loading={loadingFaculty}
                                        disabled={fieldsDisabled || isEditMode}
                                        showSearch
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                                        }
                                    >
                                        {(isCreateMode ? availableFacultyAccounts : allFacultyAccounts).map(account => (
                                            <Option key={account._id} value={account._id}>
                                                {`${account.firstName || ''} ${account.lastName || ''} (${account.employeeID || account.userID || account._id})`}
                                            </Option>
                                        ))}
                                        {isCreateMode && !loadingFaculty && availableFacultyAccounts.length === 0 && (
                                            <Option value="" disabled>No available faculty or all have profiles.</Option>
                                        )}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label="Department"
                                    name="department"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select a department!' }]}
                                >
                                    <Select placeholder="Select department" disabled={fieldsDisabled}>
                                        <Option value="ABM">ABM</Option>
                                        <Option value="STEM">STEM</Option>
                                        <Option value="HUMSS">HUMSS</Option>
                                        <Option value="GAS">GAS</Option>
                                        <Option value="TVL">TVL</Option>
                                        <Option value="General">GENERAL</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label="Designation"
                                    name="designation"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select the designation!' }]}
                                >
                                    <Select placeholder="Select designation" disabled={fieldsDisabled}>
                                        <Option value="Subject Teacher">Subject Teacher</Option>
                                        <Option value="Class Adviser">Class Adviser</Option>
                                        <Option value="Strand Coordinator">Strand Coordinator</Option>
                                        <Option value="Level Coordinator">Level Coordinator</Option>
                                        <Option value="Academic Head">Academic Head</Option>
                                        <Option value="Others">Others</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label="Employment Type"
                                    name="employmentType"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select employment type!' }]}
                                >
                                    <Select placeholder="Full-Time or Part-Time" disabled={fieldsDisabled}>
                                        <Option value="Full-Time">Full-Time</Option>
                                        <Option value="Part-Time">Part-Time</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label="Subject Classification Handled"
                                    name="subjectClassificationHandled"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select subject classification!' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Select one or more classifications"
                                        allowClear
                                        disabled={fieldsDisabled}
                                    >
                                        <Option value="CORE">CORE</Option>
                                        <Option value="APPLIED">APPLIED</Option>
                                        <Option value="STRAND">STRAND</Option>
                                        <Option value="CVF">CVF</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label="Grade Level Handled"
                                    name="gradeLevelHandled"
                                    rules={isViewMode ? [] : [{ required: true, message: 'Please select grade level(s)!' }]}
                                >
                                    <Select mode="multiple" placeholder="Select grade levels" disabled={fieldsDisabled}>
                                        <Option value="Grade 11">Grade 11</Option>
                                        <Option value="Grade 12">Grade 12</Option>
                                    </Select>
                                </Form.Item>

                                {!isViewMode && (
                                    <div className="buttons" style={{ marginTop: '20px', textAlign: 'right' }}>
                                        <Button type="default" onClick={handleBack} disabled={isSubmitting} style={{ marginRight: 8 }}>
                                            Cancel
                                        </Button>
                                        <Button type="primary" htmlType="submit" loading={isSubmitting} className={isCreateMode ? 'create-btn' : ''}>
                                            {isEditMode ? 'Update Profile' : 'Save Profile'}
                                        </Button>
                                    </div>
                                )}
                                {isViewMode && (
                                    <div className="buttons" style={{ marginTop: '20px', textAlign: 'right' }}>
                                        <Button type="default" onClick={handleBack}>
                                            Back to List
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Form>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default FacultyProfileForm;